"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { ICourse } from "@/api/admin/course";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import type { RootState } from "@/store/store";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";

import { LEAD_DROP_VARIANTS } from "../../../constants";
import SpecialisationCarouselModal from "./SpecialisationCarouselModal";
import { ArrowRightBoldIcon, BoxBoldIcon, ExportLinearIcon } from "./icons";
import {
  SPECIALISATION_CAROUSEL_CARD_ACTIONS,
  SPECIALISATION_CAROUSEL_DESIGN_VARIANTS,
} from "./types";
import type {
  SpecialisationCarouselCard,
  SpecialisationCarouselData,
  SpecialisationCarouselModalTheme,
} from "./types";

const SECTION_SLUG = "specialisation_carousel";

/** Card shadow lifted verbatim from the Figma "Smoothie Card" style. */
const CARD_SHADOW =
  "drop-shadow(0px -8.978px 10.202px rgba(0,0,0,0.05)) drop-shadow(0px 3.265px 10.202px rgba(0,0,0,0.12))";

function resolvePayload(
  data: SpecialisationCarouselProps["data"],
): SpecialisationCarouselData | undefined {
  const outer = data as { data?: SpecialisationCarouselData };
  if (outer?.data && typeof outer.data === "object") {
    // Admin preview nests one level deeper than the public page.
    const inner = outer.data as { data?: SpecialisationCarouselData };
    if (inner?.data && typeof inner.data === "object") {
      return inner.data;
    }
    return outer.data;
  }
  return data as SpecialisationCarouselData;
}

function isCardActive(card: SpecialisationCarouselCard): boolean {
  return card.meta_status !== false && card.meta_status !== 0;
}

function byPriority(
  a: SpecialisationCarouselCard,
  b: SpecialisationCarouselCard,
): number {
  return Number(a.meta_priority ?? 0) - Number(b.meta_priority ?? 0);
}

/** Same normalisation the other widgets use for admin-entered links. */
function normalizeHref(url: string): string {
  const href = (url ?? "").trim();
  if (!href) return "";
  if (
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("/") ||
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:")
  ) {
    return href;
  }
  return `/${href.replace(/^\//, "")}`;
}

interface SpecialisationCarouselProps {
  data:
    | SpecialisationCarouselData
    | { data: SpecialisationCarouselData }
    | { data: { data: SpecialisationCarouselData } };
}

const SpecialisationCarousel3000 = ({ data }: SpecialisationCarouselProps) => {
  const dispatch = useDispatch();
  const sectionData = resolvePayload(data);
  const trackRef = useRef<HTMLDivElement>(null);

  const courses = useSelector(
    (state: RootState) => state.leaddrop.courses,
  ) as ICourse[];

  const [modalCard, setModalCard] =
    useState<SpecialisationCarouselCard | null>(null);

  const {
    status = true,
    design_variant = SPECIALISATION_CAROUSEL_DESIGN_VARIANTS.VARIANT_1,
    title = "",
    title_color = "#101112",
    subtitle = "",
    subtitle_color = "#101112",
    background_color = "#FFFFFF",
    card_background_color = "#FFFFFF",
    card_title_color = "#000000",
    card_arrow_color = "#292D32",
    card_icon_color = "#292D32",
    nav_arrow_color = "#9F1C34",
    nav_prev_icon = "",
    nav_prev_icon_alt = "",
    nav_next_icon = "",
    nav_next_icon_alt = "",
    scroll_active_color = "#9F1C34",
    scroll_inactive_color = "#D9D9D9",
    scrollbar_thumb_color = "#9F1C34",
    scrollbar_track_color = "#F1F1F1",
    cta_text = "Apply Now",
    cta_text_color = "#FFFFFF",
    cta_background_color = "#9F1C34",
    cta_shadow_color = "rgba(202,125,139,0.6)",
    cta_icon = "",
    cta_icon_alt = "",
    cta_url = "",
  } = sectionData || {};

  const isIconLayout =
    String(design_variant) ===
    SPECIALISATION_CAROUSEL_DESIGN_VARIANTS.VARIANT_2;

  const showNavigation = isCmsShowFlagOn(sectionData?.show_navigation, true);
  const showPagination = isCmsShowFlagOn(sectionData?.show_pagination, true);
  const showScrollbar = isCmsShowFlagOn(sectionData?.show_scrollbar, false);
  const showCta = isCmsShowFlagOn(sectionData?.show_cta, true);

  const cards = useMemo(() => {
    const rows = Array.isArray(sectionData?.metadata)
      ? sectionData!.metadata!
      : [];
    return rows.filter(isCardActive).slice().sort(byPriority);
  }, [sectionData]);

  const modalTheme: SpecialisationCarouselModalTheme = useMemo(
    () => ({
      headerBackgroundColor:
        sectionData?.modal_header_background_color?.trim() || "#F5E4E7",
      bodyBackgroundColor:
        sectionData?.modal_body_background_color?.trim() || "#FFFFFF",
      closeIconColor: sectionData?.modal_close_icon_color?.trim() || "#9F1C34",
      closeIcon: sectionData?.modal_close_icon?.trim() || "",
      closeIconAlt: sectionData?.modal_close_icon_alt?.trim() || "",
      primaryCtaText:
        sectionData?.modal_primary_cta_text?.trim() || "Apply Now",
      primaryCtaTextColor:
        sectionData?.modal_primary_cta_text_color?.trim() || "#FFFFFF",
      primaryCtaBackgroundColor:
        sectionData?.modal_primary_cta_background_color?.trim() || "#9F1C34",
      primaryCtaIcon: sectionData?.modal_primary_cta_icon?.trim() || "",
      primaryCtaIconAlt: sectionData?.modal_primary_cta_icon_alt?.trim() || "",
      brochureCtaText:
        sectionData?.modal_brochure_cta_text?.trim() || "Download Brochure",
      brochureCtaTextColor:
        sectionData?.modal_brochure_cta_text_color?.trim() || "#000000",
      brochureCtaBackgroundColor:
        sectionData?.modal_brochure_cta_background_color?.trim() || "#FFFFFF",
      brochureCtaBorderColor:
        sectionData?.modal_brochure_cta_border_color?.trim() || "#000000",
      brochureCtaIcon: sectionData?.modal_brochure_cta_icon?.trim() || "",
      brochureCtaIconAlt:
        sectionData?.modal_brochure_cta_icon_alt?.trim() || "",
      ctaShadowColor: cta_shadow_color || "rgba(202,125,139,0.6)",
    }),
    [sectionData, cta_shadow_color],
  );

  /**
   * Resolve a card's linked global course. The lead form binds on
   * erp_unicode (falling back to id) exactly like the Enrollments widget, and
   * the brochure download link comes off the same course row — with the value
   * the CMS auto-copied onto the card as a fallback.
   */
  const resolveCourse = useCallback(
    (card: SpecialisationCarouselCard) => {
      const raw = card.meta_course_id;
      const idStr =
        raw == null || raw === "" || raw === 0 || raw === "0"
          ? ""
          : String(raw).trim();

      const match = idStr
        ? (courses ?? []).find((course) => String(course.id ?? "") === idStr)
        : undefined;

      const selectValue = match
        ? String(match.erp_unicode ?? "").trim() ||
          String(match.id ?? idStr).trim()
        : idStr;

      const brochure =
        String(match?.brochure ?? "").trim() ||
        String(card.meta_brochure_url ?? "").trim();

      return { selectValue, brochure };
    },
    [courses],
  );

  const openLeadForm = useCallback(
    (card: SpecialisationCarouselCard | null, component: string) => {
      const course = card ? resolveCourse(card).selectValue : "";
      ctaClick({
        section: SECTION_SLUG,
        component,
        label: card?.meta_title?.trim() || cta_text || "Apply Now",
        step: "1",
        entity: SECTION_SLUG,
        parentEntity: SECTION_SLUG,
      });
      dispatch(
        handleOpenLeaddrop({
          variant: LEAD_DROP_VARIANTS.REGISTER,
          ...(course ? { course, lockEnrollmentCourse: true } : {}),
        }),
      );
    },
    [cta_text, dispatch, resolveCourse],
  );

  /** Brochure download always runs through the lead form (download variant). */
  const openBrochureLeadForm = useCallback(
    (card: SpecialisationCarouselCard) => {
      const { selectValue, brochure } = resolveCourse(card);
      if (!brochure) return;
      ctaClick({
        section: SECTION_SLUG,
        component: "brochure_download",
        label: card.meta_title?.trim() || "Brochure",
        step: "",
        entity: "brochure_download",
        parentEntity: SECTION_SLUG,
        landing_url: brochure,
      });
      dispatch(
        handleOpenLeaddrop({
          variant: LEAD_DROP_VARIANTS.DOWNLOAD,
          downloadLink: brochure,
          ...(selectValue
            ? { course: selectValue, lockEnrollmentCourse: true }
            : {}),
        }),
      );
    },
    [dispatch, resolveCourse],
  );

  const handleCardClick = useCallback(
    (card: SpecialisationCarouselCard) => {
      const action = String(
        card.meta_click_action || SPECIALISATION_CAROUSEL_CARD_ACTIONS.LEAD_FORM,
      );

      if (action === SPECIALISATION_CAROUSEL_CARD_ACTIONS.MODAL) {
        ctaClick({
          section: SECTION_SLUG,
          component: "card_modal_open",
          label: card.meta_title?.trim() || "",
          step: "",
          entity: SECTION_SLUG,
          parentEntity: SECTION_SLUG,
        });
        setModalCard(card);
        return;
      }

      // The URL action falls back to the lead form when no link is configured,
      // so a half-filled card never becomes a dead click target.
      if (action === SPECIALISATION_CAROUSEL_CARD_ACTIONS.URL) {
        const href = normalizeHref(card.meta_url ?? "");
        if (href) {
          ctaClick({
            section: SECTION_SLUG,
            component: "card_link",
            label: card.meta_title?.trim() || "",
            step: "",
            entity: SECTION_SLUG,
            parentEntity: SECTION_SLUG,
            landing_url: href,
          });
          const newTab =
            card.meta_open_in_new_tab === true ||
            card.meta_open_in_new_tab === 1;
          if (newTab) {
            window.open(href, "_blank", "noopener,noreferrer");
          } else {
            window.location.href = href;
          }
          return;
        }
      }

      openLeadForm(card, "card_lead_form");
    },
    [openLeadForm],
  );

  const handleSectionCtaClick = useCallback(() => {
    const href = normalizeHref(cta_url ?? "");
    if (href) {
      ctaClick({
        section: SECTION_SLUG,
        component: "section_cta",
        label: cta_text || "Apply Now",
        step: "",
        entity: SECTION_SLUG,
        parentEntity: SECTION_SLUG,
        landing_url: href,
      });
      window.location.href = href;
      return;
    }
    openLeadForm(null, "section_cta");
  }, [cta_text, cta_url, openLeadForm]);

  // ── Carousel plumbing ───────────────────────────────────────────────
  /**
   * The track is a plain scroll container, so how far it can move is bounded by
   * its scroll range — NOT by the card count. On a wide screen most of the
   * cards are already on screen, which leaves only a couple of scroll stops:
   * paging by one card and drawing one dot per card made the track look frozen
   * (e.g. five cards stopped moving after the second dot) because every stop
   * past the right edge resolved to the same clamped scroll position.
   */
  const [metrics, setMetrics] = useState(() => ({
    /** Reachable scroll stops, i.e. how many dots the indicator draws. */
    pageCount: Math.max(1, cards.length),
    activeIndex: 0,
    isScrollable: true,
  }));

  /** Distance between two card starts at the CURRENT breakpoint. */
  const measureStep = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    const first = track.firstElementChild as HTMLElement | null;
    const second = track.children[1] as HTMLElement | undefined;
    if (!first) return 0;
    if (second) return second.offsetLeft - first.offsetLeft;
    return first.offsetWidth;
  }, []);

  /** Live scroll geometry — re-read on every use, card sizes are per-breakpoint. */
  const readGeometry = useCallback(() => {
    const track = trackRef.current;
    if (!track) return null;
    const step = measureStep();
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    // One stop per card while the track still has room, plus the final stop
    // that lands flush against the right edge.
    const pageCount =
      step <= 0 || maxScroll <= 1 ? 1 : Math.ceil(maxScroll / step) + 1;
    return { track, step, maxScroll, pageCount };
  }, [measureStep]);

  const syncMetrics = useCallback(() => {
    const geometry = readGeometry();
    if (!geometry) return;
    const { track, step, maxScroll, pageCount } = geometry;
    // The last stop is clamped to the scroll end, so it is not a multiple of
    // the step — resolve it by position instead of by rounding.
    const activeIndex =
      track.scrollLeft >= maxScroll - 1
        ? pageCount - 1
        : Math.min(
            pageCount - 1,
            Math.max(0, Math.round(track.scrollLeft / step)),
          );
    const isScrollable = maxScroll > 1;
    setMetrics((prev) =>
      prev.pageCount === pageCount &&
      prev.activeIndex === activeIndex &&
      prev.isScrollable === isScrollable
        ? prev
        : { pageCount, activeIndex, isScrollable },
    );
  }, [readGeometry]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    syncMetrics();
    track.addEventListener("scroll", syncMetrics, { passive: true });
    // Card widths change at every breakpoint, so the stop count has to come
    // from the live layout rather than from a resize listener alone.
    const observer = new ResizeObserver(syncMetrics);
    observer.observe(track);
    return () => {
      track.removeEventListener("scroll", syncMetrics);
      observer.disconnect();
    };
  }, [syncMetrics, cards.length]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const geometry = readGeometry();
      if (!geometry) return;
      const { track, step, maxScroll, pageCount } = geometry;
      const target = Math.min(Math.max(index, 0), pageCount - 1);
      track.scrollTo({
        left: Math.min(target * step, maxScroll),
        behavior: "smooth",
      });
    },
    [readGeometry],
  );

  // Paging off the resolved index (rather than the raw scrollLeft) keeps the
  // arrows usable at the clamped last stop, which sits mid-step.
  const scrollByCards = useCallback(
    (direction: -1 | 1) => scrollToIndex(metrics.activeIndex + direction),
    [metrics.activeIndex, scrollToIndex],
  );

  const canScrollPrev = metrics.activeIndex > 0;
  const canScrollNext = metrics.activeIndex < metrics.pageCount - 1;

  if (!status) return null;
  if (!cards.length) return null;

  const shellPadding =
    "px-[24px] sm:px-[40px] md:px-[60px] lg:px-[80px] xl:px-[160px]";
  // The card track keeps the design's slightly wider md inset (80 vs 60), and
  // spells out pl/pr separately so the mobile right-edge bleed below can
  // override only the right side without fighting a `px-*` shorthand.
  const trackPadding = [
    "pl-[24px] sm:pl-[40px] md:pl-[80px] lg:pl-[80px] xl:pl-[160px]",
    "pr-[24px] sm:pr-[40px] md:pr-[80px] lg:pr-[80px] xl:pr-[160px]",
    // Without a matching scroll-padding the snapport starts at the border box,
    // so a snapped card would sit flush against the edge instead of keeping
    // the section's inset.
    "scroll-pl-[24px] sm:scroll-pl-[40px] md:scroll-pl-[80px] lg:scroll-pl-[80px] xl:scroll-pl-[160px]",
  ];

  const renderCard = (card: SpecialisationCarouselCard, index: number) => {
    const cardTitle = card.meta_title?.trim() || "";
    const arrowIcon = card.meta_arrow_icon?.trim() || "";

    const arrow = arrowIcon ? (
      <Image
        src={arrowIcon}
        alt={card.meta_arrow_icon_alt?.trim() || ""}
        width={28}
        height={28}
        className={cn(
          "shrink-0 object-contain",
          isIconLayout ? "size-[24px]" : "size-[28px]",
        )}
      />
    ) : (
      <ArrowRightBoldIcon
        color={card_arrow_color}
        className={cn("shrink-0", isIconLayout ? "size-[24px]" : "size-[28px]")}
      />
    );

    const body = isIconLayout ? (
      // ── variant_2: icon card ──────────────────────────────────────
      <>
        {card.meta_icon?.trim() ? (
          <Image
            src={card.meta_icon.trim()}
            alt={card.meta_icon_alt?.trim() || ""}
            width={64}
            height={64}
            className="size-[44px] shrink-0 object-contain md:size-[54px] lg:size-[64px]"
          />
        ) : (
          <BoxBoldIcon
            color={card_icon_color}
            className="size-[44px] shrink-0 md:size-[54px] lg:size-[64px]"
          />
        )}
        <div className="flex w-full items-end justify-center gap-[8.162px]">
          <p
            className={cn(
              "min-w-px flex-1 [word-break:break-word]",
              "text-[14px] leading-[1.5] font-semibold sm:text-[16px] lg:text-[18px]",
            )}
            style={{ color: card_title_color }}
          >
            {cardTitle}
          </p>
          {arrow}
        </div>
      </>
    ) : (
      // ── variant_1: image card ─────────────────────────────────────
      <>
        <div className="relative min-h-px w-full flex-1 overflow-hidden rounded-[14px]">
          {card.meta_image?.trim() ? (
            <Image
              src={card.meta_image.trim()}
              alt={card.meta_image_alt?.trim() || cardTitle}
              fill
              sizes="(max-width: 1080px) 249px, (max-width: 1430px) 287px, 306px"
              className="pointer-events-none rounded-[14px] object-cover"
            />
          ) : (
            <div className="size-full rounded-[14px] bg-black/5" />
          )}
        </div>
        <div className="flex w-full items-center gap-[10px]">
          <div
            className={cn(
              "flex h-[48px] min-w-px flex-1 flex-col justify-center [word-break:break-word]",
              "text-[14px] leading-[1.5] font-semibold lg:text-[16px] xl:text-[18px]",
            )}
            style={{ color: card_title_color }}
          >
            <p className="leading-[1.5]">{cardTitle}</p>
          </div>
          {arrow}
        </div>
      </>
    );

    return (
      <button
        key={card.id ?? index}
        type="button"
        onClick={() => handleCardClick(card)}
        aria-label={cardTitle}
        className={cn(
          "flex shrink-0 snap-start flex-col rounded-[16px] text-left outline-none",
          "transition-transform duration-200 hover:-translate-y-[4px] focus-visible:ring-2 focus-visible:ring-black/30",
          isIconLayout
            ? [
                "items-start justify-between py-[16px] pr-[10px] pl-[16px] md:py-[24px] md:pl-[24px]",
                "h-[182px] w-[200px] sm:h-[200px] sm:w-[220px] md:h-[220px] md:w-[242px] lg:h-[236px] lg:w-[260px] xl:h-[250px] xl:w-[275px]",
              ]
            : [
                "items-start gap-[20px] px-[12px] pt-[12px] pb-[20px]",
                "h-[260px] w-[249px] md:h-[280px] md:w-[268px] lg:h-[300px] lg:w-[287px] xl:h-[320px] xl:w-[306px]",
              ],
        )}
        style={{
          background: card_background_color,
          filter: CARD_SHADOW,
        }}
      >
        {body}
      </button>
    );
  };

  return (
    <section
      data-specialisation-carousel={isIconLayout ? "variant_2" : "variant_1"}
      className="w-full py-[20px] sm:py-[40px] lg:py-[60px] [&_*]:![font-family:Poppins,sans-serif]"
      style={
        {
          background: background_color,
          "--sc-thumb": scrollbar_thumb_color,
          "--sc-track": scrollbar_track_color,
        } as CSSProperties
      }
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      {title || subtitle ? (
        <div className={cn("mx-auto w-full max-w-[1750px]", shellPadding)}>
          <div className="flex flex-col items-center gap-[16px] text-center sm:gap-[9.677px]">
            {title ? (
              <h2
                className={cn(
                  "w-full [word-break:break-word]",
                  "text-[28px] leading-[1.25] font-semibold tracking-[-0.42px]",
                  "md:text-[32px] md:tracking-[-0.48px] lg:text-[36px] lg:tracking-[-0.54px]",
                )}
                style={{ color: title_color }}
              >
                {title}
              </h2>
            ) : null}
            {subtitle ? (
              <p
                className={cn(
                  "w-full [word-break:break-word]",
                  "text-[14px] leading-[1.65] font-medium",
                  "sm:text-[16px] sm:leading-[1.6] sm:font-normal",
                  "md:leading-[1.65] lg:text-[17px] lg:font-medium",
                  "xl:text-[18px] xl:font-normal",
                )}
                style={{ color: subtitle_color }}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ── Card track ─────────────────────────────────────────────── */}
      <div
        ref={trackRef}
        className={cn(
          "mx-auto flex w-full max-w-[1750px] snap-x snap-mandatory overflow-x-auto scroll-smooth",
          "gap-[16px] xl:gap-[24px]",
          // Cards carry a drop-shadow; the vertical padding keeps it from being
          // clipped by the scroll container, and the top margin is reduced by
          // the same amount so the gap to the header stays 40 / 60.
          "mt-[28px] py-[12px] xl:mt-[48px]",
          trackPadding,
          // On mobile the track bleeds to the viewport edge so the next card
          // peeks exactly as it does in the design.
          "-mr-[24px] sm:mr-0",
          showScrollbar
            ? [
                "[&::-webkit-scrollbar]:h-[6px]",
                "[&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[var(--sc-track)]",
                "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--sc-thumb)]",
                "[scrollbar-color:var(--sc-thumb)_var(--sc-track)] [scrollbar-width:thin]",
              ]
            : "[&::-webkit-scrollbar]:hidden [scrollbar-width:none]",
        )}
      >
        {cards.map(renderCard)}
      </div>

      {/* ── Navigation + scroll indicator ──────────────────────────── */}
      {/* Nothing to page through once every card fits, so the whole row goes. */}
      {(showNavigation || showPagination) && metrics.isScrollable ? (
        <div
          className={cn(
            // 28 + the track's 12px bottom padding = the design's 40 (60 at xl).
            "mx-auto mt-[28px] flex w-full max-w-[1750px] items-center justify-center gap-[24px] xl:mt-[48px]",
            shellPadding,
          )}
        >
          {showNavigation ? (
            <button
              type="button"
              onClick={() => scrollByCards(-1)}
              disabled={!canScrollPrev}
              aria-label="Previous"
              className={cn(
                "flex shrink-0 items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-black/30",
                "transition-opacity disabled:cursor-not-allowed disabled:opacity-40",
              )}
            >
              {nav_prev_icon?.trim() ? (
                <Image
                  src={nav_prev_icon.trim()}
                  alt={nav_prev_icon_alt?.trim() || "Previous"}
                  width={24}
                  height={24}
                  className="size-[24px] object-contain"
                />
              ) : (
                <span className="block rotate-180">
                  <ArrowRightBoldIcon
                    color={nav_arrow_color}
                    className="size-[24px]"
                  />
                </span>
              )}
            </button>
          ) : null}

          {showPagination ? (
            <div className="flex items-center gap-[5px]">
              {Array.from({ length: metrics.pageCount }, (_, index) => {
                const isActive = index === metrics.activeIndex;
                return (
                  <button
                    key={`dot-${index}`}
                    type="button"
                    onClick={() => scrollToIndex(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-current={isActive ? "true" : undefined}
                    className={cn(
                      "h-[10px] rounded-full transition-all duration-200 outline-none",
                      isActive ? "w-[40px]" : "w-[10px]",
                    )}
                    style={{
                      background: isActive
                        ? scroll_active_color
                        : scroll_inactive_color,
                    }}
                  />
                );
              })}
            </div>
          ) : null}

          {showNavigation ? (
            <button
              type="button"
              onClick={() => scrollByCards(1)}
              disabled={!canScrollNext}
              aria-label="Next"
              className={cn(
                "flex shrink-0 items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-black/30",
                "transition-opacity disabled:cursor-not-allowed disabled:opacity-40",
              )}
            >
              {nav_next_icon?.trim() ? (
                <Image
                  src={nav_next_icon.trim()}
                  alt={nav_next_icon_alt?.trim() || "Next"}
                  width={24}
                  height={24}
                  className="size-[24px] object-contain"
                />
              ) : (
                <ArrowRightBoldIcon
                  color={nav_arrow_color}
                  className="size-[24px]"
                />
              )}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* ── Section CTA ────────────────────────────────────────────── */}
      {showCta && cta_text?.trim() ? (
        <div
          className={cn(
            "mx-auto mt-[40px] flex w-full max-w-[1750px] justify-center xl:mt-[60px]",
            shellPadding,
          )}
        >
          <button
            type="button"
            onClick={handleSectionCtaClick}
            className={cn(
              "flex w-full items-center justify-center gap-[10px] rounded-[8px] px-[24px] py-[16px]",
              "text-[16px] leading-[1.4] font-semibold whitespace-nowrap",
              "transition-transform hover:scale-[1.02] sm:w-auto",
            )}
            style={{
              background: cta_background_color,
              color: cta_text_color,
              filter: `drop-shadow(0px 10px 10px ${cta_shadow_color})`,
            }}
          >
            {cta_text}
            {cta_icon?.trim() ? (
              <Image
                src={cta_icon.trim()}
                alt={cta_icon_alt?.trim() || ""}
                width={24}
                height={24}
                className="size-[24px] shrink-0 object-contain"
              />
            ) : (
              <ExportLinearIcon
                color={cta_text_color}
                className="size-[24px] shrink-0"
              />
            )}
          </button>
        </div>
      ) : null}

      <SpecialisationCarouselModal
        card={modalCard}
        open={Boolean(modalCard)}
        onOpenChange={(next) => {
          if (!next) setModalCard(null);
        }}
        theme={modalTheme}
        brochureLink={modalCard ? resolveCourse(modalCard).brochure : ""}
        onPrimaryCtaClick={(card) => {
          setModalCard(null);
          openLeadForm(card, "modal_apply_cta");
        }}
        onBrochureClick={(card) => {
          setModalCard(null);
          openBrochureLeadForm(card);
        }}
      />
    </section>
  );
};

export default SpecialisationCarousel3000;
